// BlockNote invokes this through the block spec's `render`, so it is a React
// component and hooks are valid here.
import type { BlockConfig } from '@blocknote/core';
import type { ReactCustomBlockRenderProps } from '@blocknote/react';
import * as React from 'react';

import type { dropdownBlockPropSchema } from './dropdown-block-config';
import { deriveDropdownBlockView } from './dropdown-block-view-model';
import {
  EditableDropdown,
  EditableFallback,
  ReadOnlyLine,
} from './dropdown-block-views';
import { parseDropdownBlockOptions } from './dropdown-options';
import { useDropdownBlockState } from './use-dropdown-block-state';

/** Render props BlockNote passes for the `dropdown` block spec. */
type DropdownBlockRenderProps = ReactCustomBlockRenderProps<
  BlockConfig<'dropdown', typeof dropdownBlockPropSchema, 'none'>
>;

/**
 * Renderer for the dropdown block. Kept as a standalone component (rather than
 * inlined in the spec's `render`) so its hooks run behind a real component
 * boundary and BlockNote defers position resolution until after mount — see
 * {@link useDropdownBlockState}. Owns render orchestration only; the label /
 * display / className derivation lives in {@link deriveDropdownBlockView} and
 * the presentational variants in `./dropdown-block-views`.
 */
export const DropdownBlockRenderer: React.FC<DropdownBlockRenderProps> = (
  props,
) => {
  // Extract primitive values for dependency tracking
  // BlockNote props are stable references, but we extract primitives to satisfy linter
  const blockId = props.block?.id;
  const optionsString = props.block?.props?.options;
  const blockValue = props.block?.props?.value;

  const { isReady, isRemoved, currentValue, setCurrentValue } =
    useDropdownBlockState({
      blockId,
      blockValue,
      blockProps: props.block?.props,
    });

  // Parse options from JSON string - MUST be called before any conditional returns
  // This hook must always be called in the same order
  const options = React.useMemo(
    () => parseDropdownBlockOptions(optionsString),
    [optionsString],
  );

  // Safety check: ensure block and editor are available
  // Also check that block has an id (required by BlockNote's internal code)
  if (!props.block || !props.editor || !props.block.id || isRemoved) {
    // Return null instead of a div to avoid rendering issues during removal
    return null;
  }

  // Check if editor is editable - disable dropdown if not editable
  const isEditable = props.editor?.isEditable ?? true;

  // Show fallback UI until ready to prevent position resolution errors
  if (!isReady) {
    const { label, displayValue, optionClassName } = deriveDropdownBlockView(
      props.block.props?.value || '',
      props.block.props?.label,
      options,
    );

    if (!isEditable) {
      return (
        <ReadOnlyLine
          label={label}
          displayValue={displayValue}
          optionClassName={optionClassName}
        />
      );
    }

    return (
      <EditableFallback
        label={label}
        displayValue={displayValue}
        optionClassName={optionClassName}
      />
    );
  }

  // Additional safety check before accessing block properties
  // This prevents errors when block is being removed
  if (!props.block.props) {
    return null;
  }

  // Use local state value to ensure UI updates when value changes
  const selectedValue = currentValue;
  const { label, displayValue, optionClassName } = deriveDropdownBlockView(
    selectedValue,
    props.block.props?.label,
    options,
  );

  const handleValueChange = (newValue: string) => {
    // Don't allow changes if editor is not editable
    if (!isEditable) {
      return;
    }

    try {
      // Update local state immediately for instant UI feedback
      setCurrentValue(newValue);

      // Double-check block still exists before updating
      if (!props.block || !props.block.id || !props.editor) {
        return;
      }
      props.editor.updateBlock(props.block, {
        type: 'dropdown',
        props: {
          ...props.block.props,
          value: newValue,
        },
      });
    } catch (error) {
      // Silently handle errors during block removal
      if (
        error instanceof Error &&
        (error.message.includes('Cannot find node position') ||
          error.message.includes('node position') ||
          error.message.includes('Cannot read properties'))
      ) {
        // Block is being removed, ignore the error
        return;
      }
    }
  };

  if (!isEditable) {
    return (
      <ReadOnlyLine
        label={label}
        displayValue={displayValue}
        optionClassName={optionClassName}
      />
    );
  }

  return (
    <EditableDropdown
      label={label}
      selectedValue={selectedValue}
      displayValue={displayValue}
      optionClassName={optionClassName}
      options={options}
      onValueChange={handleValueChange}
    />
  );
};
